/**
 * Fleet dashboard card contract types.
 * Data comes from useFleetSystems (real Supabase: properties, plants, get_latest_plant_telemetry, overnight_forecasts).
 */

export type ProviderType = 'solark' | 'eg4' | 'franklinwh' | 'solaredge' | 'enphase' | 'tesla' | 'solis' | 'unknown';

/** Display names for fleet cards, filters, and badges. Unknown = catch-all for unrecognized provider_type. */
export const PROVIDER_DISPLAY_NAMES: Record<ProviderType, string> = {
  solark: 'Sol-Ark',
  eg4: 'EG4',
  franklinwh: 'FranklinWH',
  solaredge: 'SolarEdge',
  enphase: 'Enphase',
  tesla: 'Tesla',
  solis: 'Solis',
  unknown: 'Unknown',
};

export type BatteryState = 'charging' | 'discharging' | 'idle';
export type ConfidenceLevel = 'high' | 'medium' | 'low';
export type GridMode = 'off_grid' | 'grid_primary';

export interface FleetSystem {
  /** Property ID — used for navigation to /app/property/:id */
  id: string;
  systemName: string;
  location: { city: string; state: string };
  provider: ProviderType;
  /** Online = poll succeeded recently (within STALE_MINUTES) */
  online: boolean;
  /** Data stale = last successful poll > 15 min ago */
  dataStale: boolean;
  /** Last poll time (from energy_systems); null if never polled */
  lastPollAt: string | null;
  /** Last poll result: 'success' | error string; null if never polled */
  lastPollStatus: string | null;
  /** When offline, normalized reason: credential error | timeout | API unavailable */
  lastPollError?: string | null;
  /** True when property has a current overnight forecast (forecast_end > now). */
  hasOvernightForecast: boolean;
  /** At risk = &lt;10 kWh at sunrise; only for off_grid. Overnight Risk metric counts only online systems. */
  forecastRisk: boolean;
  /** off_grid | grid_primary. NULL = treat as off_grid temporarily. */
  gridMode: GridMode | null;
  /** Solar kW — same source as property dashboard (get_latest_plant_telemetry) */
  pvKw: number;
  /** Battery kW display: + = charging, − = discharging (normalized from raw telemetry). Same as property EnergyFlow. */
  battKw: number;
  /** Load kW */
  loadKw: number;
  /** Grid kW (export positive); aligns with property EnergyFlow */
  gridKw: number | null;
  /** Generator kW; aligns with property EnergyFlow */
  genKw: number | null;
  batteryState: BatteryState;
  socPct: number;
  /** Property capacity (kWh) for energy-context display; when set, show "X.X kWh (YY%)" per ENERGY_SEMANTICS. */
  batteryCapacityKwh?: number;
  /** Available energy (kWh) = capacity × (soc/100); same semantics as Property dashboard Available Energy. */
  availableKwh?: number;
  /** Overnight projection: kWh at sunrise — same source as property (overnight_forecasts) */
  overnightKwhAtSunrise: number;
  confidence: ConfidenceLevel;
  /** Phase 2: grid_primary only; resilience risk active from property_resilience_status */
  resilienceRisk?: boolean;
  /** Phase 2: 'warning' | 'critical' when resilienceRisk */
  resilienceSeverity?: 'warning' | 'critical';

  /** Reserve threshold (kWh); from property battery_reserve_pct × capacity. */
  reserve_kwh: number;
  /** Projected minimum SOC % before sunrise (from forecast runway when available). */
  projected_min_soc_pct: number;
  /** ISO time when SOC projected to cross reserve; null if not projected. */
  projected_breach_time: string | null;
  /** Probability of reserve breach 0–1 (from forecast when available). */
  breach_probability: number;
  /** PV underperformance signal 0–1. */
  pv_underperformance_score: number;
  /** Curtailment signal 0–1. */
  curtailment_score: number;
  /** Forecast confidence for display. */
  forecast_confidence: 'low' | 'medium' | 'high';
  /** Why this system is in this state (primary; optional secondary). */
  why: { primary: string; secondary?: string };

  /** Current SOC in kWh (capacity × soc/100). */
  current_soc_kwh: number;
  /** Curtailment: battery full + PV wasted; set when signal available. */
  is_curtailing?: boolean;
  /** Wasted kWh today when curtailing. */
  wasted_kwh_today?: number;
  /** PV underperformance: actual < expected clear-sky. */
  is_underperforming?: boolean;
  /** Underperformance % (e.g. 35 = 35% below expected). */
  underperformance_pct?: number;
  /** WT-341 v3 honest PV health state (null until compute-pv-health has run). Distinguishes real
   *  underperformance from dead/partial telemetry so a telemetry problem never renders as "producing normally". */
  pvState?: string | null;
  /** Last non-empty telemetry time (from fleet snapshot agg_es). */
  lastTelemetryAt?: string | null;
  /** Hours until depletion at current load (when load > 0); null when not applicable. From fleet snapshot. */
  timeToDepletionHours?: number | null;
  /** Billing mode: 'direct' or 'reseller'. From property. */
  billingMode?: 'direct' | 'reseller';
  /** Reseller billing status. From property. */
  resellerBillingStatus?: 'none' | 'pending' | 'active' | 'past_due' | 'canceled' | 'trialing';
  /** True when DB telemetry exists for this system. False = no telemetry rows yet (values are defaults, not real). */
  hasTelemetry?: boolean;
  /** Grid import today (kWh) from daily_energy_summary. */
  gridImportKwhToday?: number;
  /** Grid export today (kWh) from daily_energy_summary. */
  gridExportKwhToday?: number;
}
